import React, { useImperativeHandle } from 'react';
import classnames from 'classnames';
import { Table, TableProps, TableAddon, TableColumn } from 'tea-component';
import AddableTable from '@gp/components/AddableTable';
import useDPSReportModule from '../../../_hooks/useDPSReportModule';
import { generateMockId, getRecordKeyOfMockable } from '@gp/utils/common';
import { getColumnsEditable } from './utils';
import { BoolFlag } from '@gp/enums/common';

import type { IBaseProps } from '@home/types/common';
import type { IDPSReportCalendarProjectSimpleVO } from '@gp/types/dpsReport/common';
import type { IDPSReportProjectCommentsUpdatesVO } from '@gp/types/dpsReport/v3';

// CommentTables组件的实例类型
export interface ICommentTablesInstance {
  // 删除Action
  delete: () => void;
}

interface ICommentTablesProps extends IBaseProps, TableProps {
  // 是否表格内部loading
  isInnerLoading?: boolean;
  // 是否禁用Add按钮
  disabledAddBtn?: boolean;
  // Add按钮的hover提示
  addBtnTips?: string | React.ReactElement | null;
  // Project下拉筛选器options数据源
  projects?: IDPSReportCalendarProjectSimpleVO[] | null;
  // 表格当前勾选的数据
  selection: string[];
  // 表格当前勾选的数据 setter
  setSelection: React.Dispatch<React.SetStateAction<string[]>>;
  // 模块数据
  value: IDPSReportProjectCommentsUpdatesVO[];
  // 模块数据更新，删除后立马更新，新增后 需要等新增这条数据编辑完整后 才会更新，页面就不需要考虑不完整的脏数据了
  onChange?: (payload: IDPSReportProjectCommentsUpdatesVO[]) => void;
}

const { selectable } = Table.addons;

export const buildRecord = (): IDPSReportProjectCommentsUpdatesVO => ({
  mock_id: generateMockId(),
  is_custom: BoolFlag.False,
  project_id: '',
  project: '',
  platform: [],
  image_name: '',
  cover: '',
  priority: '',
  comment: '',
});

const CommentTables: React.ForwardRefRenderFunction<ICommentTablesInstance, ICommentTablesProps> = (props, ref) => {
  const {
    style,
    className,
    isInnerLoading,
    disabledAddBtn,
    addBtnTips,
    projects,
    selection,
    setSelection,
    value,
    onChange,
  } = props;

  const { records, setRecords, onAdd, onDelete } = useDPSReportModule<IDPSReportProjectCommentsUpdatesVO>({
    buildRecord,
    value,
    onChange,
  });

  const addons: TableAddon<IDPSReportProjectCommentsUpdatesVO>[] = [
    selectable({
      all: Array.isArray(records) && records.length > 0,
      value: selection,
      onChange: setSelection,
    }),
  ];

  const columns: TableColumn<IDPSReportProjectCommentsUpdatesVO>[] = getColumnsEditable({
    projects,
    records,
    setRecords,
    onChange,
  });

  const handleDelete = () => {
    if (!Array.isArray(selection)) return;
    setSelection([]);
    onDelete?.([...selection]);
  };

  useImperativeHandle(ref, () => ({
    delete: handleDelete,
  }));

  return (
    <AddableTable
      style={style}
      className={classnames('dbg-table-vertical-align-top dbg-dps-report-project-comments-updates-table', className)}
      tableClassName="dbg-selectable-table"
      isInnerLoading={isInnerLoading}
      disabledAddBtn={disabledAddBtn}
      addBtnTips={addBtnTips}
      addons={addons}
      columns={columns}
      records={records}
      recordKey={getRecordKeyOfMockable}
      onAdd={onAdd}
    />
  );
};

// @ts-ignore
export default React.forwardRef(CommentTables);
